import { numberComparator, stringComparator } from "lib/utils"
import { IFormFilterSession, SessionInfo, SessionInfoCamel, ComparatorSessionType } from "types/sessions"
import { SORTORDER, SortOrder } from "types/sort"
import { FilterStatus, SessionsStatus } from "layouts/sessions/utils/const"
import { AlertType } from "layouts/charging-rules/utils/utils.ts"

export function NormalizeSession(data:SessionInfo): SessionInfoCamel {
    return {
        id: data.id,
        status: data.status,
        formattedDate: 'formatted_date' in data ? data.formatted_date : data.formattedDate,
        startingDate: 'starting_date' in data ? data.starting_date : data.startingDate,
        stoppedDate: 'stopped_date' in data ? data.stopped_date : data.stoppedDate,
        rfidNumber: 'rfid_number' in data ? data.rfid_number : data.rfidNumber,
        rfidUuid: 'rfid_uuid' in data ? data.rfid_uuid: data.rfidUuid,
        cardHolder: 'card_holder' in data ? data.card_holder : data.cardHolder,
        stationName: 'station_name' in data ? data.station_name : data.stationName,
        stationAddress: 'station_address' in data ? data.station_address : data.stationAddress,
        kwh: data.kwh,
        price: data.price,
        triggeredAlerts: 'triggered_alerts' in data ? data.triggered_alerts : data.triggeredAlerts,
        idTagInfo: 'id_tag_info' in data ? data.id_tag_info : data.idTagInfo,
        autocharge: data.autocharge ?? false,
    }
}

export function sessionSort(
    order: SortOrder,
    comparator: ComparatorSessionType
) {
    return (s1: SessionInfo, s2: SessionInfo) => {
        const res = comparator(s1, s2)

        // Historical behavior: DESC uses comparator as-is; ASC inverts it
        return order === SORTORDER.DESC ? res : -res
    }
}

export const dateSessionComparator = (s1: SessionInfo, s2: SessionInfo) => {
    const date1 = 'startingDate' in s1 ? s1.startingDate : s1.starting_date
    const date2 = 'startingDate' in s2 ? s2.startingDate : s2.starting_date
    return numberComparator(date1, date2)
}

export const statusSessionComparator = (s1: SessionInfo, s2: SessionInfo) => {
    return stringComparator(s1.status, s2.status)
}

export const rfidSessionComparator = (s1: SessionInfo, s2: SessionInfo) => {
    const rfidNumber1 = 'rfidNumber' in s1 ? s1.rfidNumber : s1.rfid_number
    const rfidNumber2 = 'rfidNumber' in s2 ? s2.rfidNumber : s2.rfid_number
    return stringComparator(rfidNumber1, rfidNumber2)
}
export const cardHolderSessionComparator = (
    s1: SessionInfo,
    s2: SessionInfo
) => {
    const cardHolder1 = 'cardHolder' in s1 ? s1.cardHolder : s1.card_holder
    const cardHolder2 = 'cardHolder' in s2 ? s2.cardHolder : s2.card_holder
    return stringComparator(cardHolder1,cardHolder2)
}
export const addressSessionComparator = (s1: SessionInfo, s2: SessionInfo) => {
    const name1 = 'stationName' in s1 ? s1.stationAddress : s1.station_address
    const name2 = 'stationName' in s2 ? s2.stationAddress : s2.station_address
    return stringComparator(name1, name2)
}

export const kwhSessionComparator = (s1: SessionInfo, s2: SessionInfo) => {
    return numberComparator(s1.kwh, s2.kwh)
}
export const priceSessionComparator = (s1: SessionInfo, s2: SessionInfo) => {
    return numberComparator(s1.price ?? 0, s2.price ?? 0)
}

const checkWeightOfAlerts = (alert: AlertType) => {
    switch (alert) {
        case AlertType.DOWAlert:
            return 1000;
        case AlertType.HoursAlert:
            return 100;
        case AlertType.MaxDuration:
            return 10;
        case AlertType.MaxKWH:
            return 1;
        default:
            return 0;
    }
}

const customAlertsArrayComparator = (array1: ReadonlyArray<AlertType>, array2: ReadonlyArray<AlertType>) => {
    let weight_1 = 0
    let weight_2 = 0

    array1.forEach(value => {
        weight_1 += checkWeightOfAlerts(value);
    })
    array2.forEach(value => {
        weight_2 += checkWeightOfAlerts(value);
    })

    if (weight_1 > weight_2) {
        return -1
    } else if (weight_1 < weight_2) {
        return 1
    } else {
        return 0
    }
}

export const alertsTriggerSessionComparator = (s1: SessionInfo, s2: SessionInfo) => {
    const alert1 = 'triggeredAlerts' in s1 ? s1.triggeredAlerts : s1.triggered_alerts
    const alert2 = 'triggeredAlerts' in s2 ? s2.triggeredAlerts : s2.triggered_alerts
    const startDate1 = 'startingDate' in s1 ? s1.startingDate : s1.starting_date
    const startDate2 = 'startingDate' in s2 ? s2.startingDate : s2.starting_date
    const alertComp: number = customAlertsArrayComparator(alert1, alert2)

    if (alertComp === 0) {
        if (startDate1 > startDate2) {
            return 1
        } else if (startDate1 < startDate2) {
            return -1
        }
    }
    return alertComp
}

export const filterSessions = (filterValues: IFormFilterSession) => {
    const { date_from, date_to, rfid_number, card_holder, status } = filterValues
    const dayDurationInMilliseconds = 1000 * 60 * 60 * 24

    // Normalize filter inputs to be null-safe and flexible (each criterion is optional)
    const hasFrom = !!date_from
    const hasUntil = !!date_to
    const safeFrom = hasFrom ? Number(date_from) : null
    const safeUntil = hasUntil ? Number(date_to) : null

    const safeRfidFilter = (rfid_number ?? "").toString().toLowerCase()
    const safeHolderFilter = (card_holder ?? "").toString().toLowerCase()

    return (session: SessionInfo) => {
        const startingDate =
            'startingDate' in session
                ? new Date(session.startingDate).getTime()
                : new Date(session.starting_date).getTime()
        const cardHolder = 'cardHolder' in session ? session.cardHolder : session.card_holder
        const rfidNumber = 'rfidNumber' in session ? session.rfidNumber : session.rfid_number

        const fromDateMatch = safeFrom === null ? true : startingDate >= safeFrom
        // Make the upper bound inclusive for the selected end day
        const untilInclusive = safeUntil === null ? null : safeUntil + dayDurationInMilliseconds - 1
        const untilDateMatch = untilInclusive === null ? true : startingDate <= untilInclusive

        const holderText = (cardHolder ?? "").toString().toLowerCase()
        const rfidText = (rfidNumber ?? "").toString().toLowerCase()

        const driverNameMatch = holderText.includes(safeHolderFilter)
        const rfidMatch = rfidText.includes(safeRfidFilter)

        const statusConditions: Record<FilterStatus, boolean> = {
            [FilterStatus.InProgress]: [
                SessionsStatus.Pending,
                SessionsStatus.Active,
            ].includes(session.status as SessionsStatus),
            [FilterStatus.Finished]: session.status === SessionsStatus.Finished,
            [FilterStatus.Failed]: [
                SessionsStatus.Failed,
                SessionsStatus.Expired,
                SessionsStatus.Unknown,
            ].includes(session.status as SessionsStatus),
            [FilterStatus.All]: true,
        }

        const statusKey = (Object.values(FilterStatus) as string[]).includes(status as FilterStatus)
            ? (status as FilterStatus)
            : FilterStatus.All
        const statusMatch = statusConditions[statusKey]

        return (
            fromDateMatch &&
            untilDateMatch &&
            driverNameMatch &&
            rfidMatch &&
            statusMatch
        )
    }
}


const countryToCurrency: Record<string, string> = {
    FR: 'EUR',
    US: 'USD',
    GB: 'GBP',
    DE: 'EUR',
    JP: 'JPY',
    CN: 'CNY',
    ES: 'EUR',
    IT: 'EUR',
    CA: 'CAD',
    BR: 'BRL',
};

export const getCurrencyFromLocale = (locale: string): string => {
    const countryCode = locale.split('-')[1];
    return countryToCurrency[countryCode] || 'EUR';
};


/**
 * Format a price with 2 decimal places according to the specified locale
 * @param value - The number to format
 * @param locale - The locale to use for formatting (default: 'fr-FR')
 * @returns The formatted price as a string
 */
export const formatPrice = (
    value: number,
    locale: string = 'fr-FR',
): string => {

    const currency = getCurrencyFromLocale(locale);

    return new Intl.NumberFormat(locale, {
        style: 'currency',
        currency,
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
    }).format(value);
};

export function threeMonthsBeforeStartOfMonth(timestampMs: number): number {
    const date = new Date(timestampMs);
    const year = date.getUTCFullYear();
    const month = date.getUTCMonth() - 3;

    return Date.UTC(year, month, 1, 0, 0, 0, 0);
}
